import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import Modal from '../components/ui/Modal';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import FilterBar from '../components/ui/FilterBar';
import ActionButton from '../components/ui/ActionButton';
import { useDOA } from '../hooks/useDOA';
import { useEntities } from '../hooks/useEntities';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';

const API = `${API_V1}/purchase-orders`;
const PAGE_SIZE = 50;

interface POLine { id: number; line_number: number; material_code: string; description: string; quantity: number; unit_price: number; line_amount: number; gl_account: string; received_quantity: number; }
interface PO { id: number; po_number: string; vendor_id: string; vendor_name: string; total_amount: number; lifecycle_status: string; entity_code: string; currency: string; buyer: string; version: number; created_at: string; approved_at: string; issued_at: string; lines: POLine[]; }
interface Summary { total: number; draft: number; pending_approval: number; approved: number; issued: number; open_amount: number; }

export default function PurchaseOrders() {
  const { canApprove } = useDOA();
  const entities = useEntities();
  const { entity } = useGlobalEntity();
  const [pos, setPOs] = useState<PO[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [form, setForm] = useState({ po_number: '', vendor_id: '', vendor_name: '', entity_code: '', currency: 'USD', contract_id: '', lines: [{ description: '', quantity: '', unit_price: '', gl_account: '', material_code: '' }] });
  const [vendors, setVendors] = useState<any[]>([]);
  const [contracts, setContracts] = useState<any[]>([]);
  const [priceList, setPriceList] = useState<any[]>([]);
  const [glAccounts, setGlAccounts] = useState<any[]>([]);
  const [approvalHistory, setApprovalHistory] = useState<any[]>([]);

  const showToast = (msg: string, ok: boolean) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (statusFilter) p.set('lifecycle_status', statusFilter);
    try {
      const [listRes, sumRes] = await Promise.all([
        authFetch(withEntityParam(`${API}?${p}`, entity)),
        authFetch(withEntityParam(`${API}/summary`, entity)),
      ]);
      if (listRes.ok) { const d = await listRes.json(); setPOs(d.items || []); setTotal(d.total || 0); }
      if (sumRes.ok) { setSummary(await sumRes.json()); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, statusFilter, entity]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    try {
      const payload = { ...form, po_number: form.po_number || undefined, lines: form.lines.filter(l => l.description).map(l => ({ material_code: l.material_code, description: l.description, quantity: parseFloat(l.quantity) || 0, unit_price: parseFloat(l.unit_price) || 0, gl_account: l.gl_account })) };
      const res = await authFetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (res.ok) {
        const d = await res.json();
        const warnings = d.gl_warnings || d.dq_warnings || [];
        if (warnings.length > 0) {
          showToast(`PO created with warnings: ${warnings[0]}`, false);
          // Show all warnings as alerts
          warnings.forEach((w: string) => console.warn('GL Warning:', w));
        } else {
          showToast('PO created', true);
        }
        setShowCreate(false); load();
      }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Failed', false); }
  };

  const handleAction = async (id: number, action: string, version: number) => {
    try {
      const res = await authFetch(`${API}/${id}/${action}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ expected_version: version }) });
      if (res.ok) { showToast(`PO ${action.replace('-', ' ')}`, true); load(); }
      else { const e = await res.json(); showToast(e.detail || 'Error', false); }
    } catch { showToast('Action failed', false); }
  };

  const addLine = () => setForm({ ...form, lines: [...form.lines, { description: '', quantity: '', unit_price: '', gl_account: '', material_code: '' }] });
  const updateLine = (i: number, field: string, value: string) => { const lines = [...form.lines]; (lines[i] as any)[field] = value; setForm({ ...form, lines }); };

  // Load vendors when entity changes
  const loadVendors = async (entityCode: string) => {
    try {
      const res = await authFetch(`${API_V1}/vendor-master?lifecycle_status=ACTIVE&limit=200`);
      if (res.ok) { const d = await res.json(); setVendors(d.items || []); }
    } catch { }
    setContracts([]); setPriceList([]);
  };

  // Load contracts when vendor changes
  const loadContracts = async (vendorId: string) => {
    try {
      const res = await authFetch(`${API_V1}/contracts?counterparty_id=${encodeURIComponent(vendorId)}&lifecycle_status=ACTIVE&limit=50`);
      if (res.ok) { const d = await res.json(); setContracts(d.items || []); }
    } catch { }
    setPriceList([]);
  };

  // Load price list when contract selected
  const loadPriceList = async (contractId: string) => {
    try {
      const res = await authFetch(`${API_V1}/contracts/extraction/${contractId}`);
      if (res.ok) {
        const d = await res.json();
        const items = d.price_list || [];
        setPriceList(items);
        // Auto-populate lines from price list. Quantity now reads
        // from the extracted price_list row instead of hardcoded '1'
        // (2026-05-21 fix -- prior code lost the contract qty even
        // when Claude AI extracted it correctly; user had to retype
        // 50/20/200/30 etc. before the PO numbers would balance).
        if (items.length > 0) {
          setForm(prev => ({
            ...prev,
            contract_id: contractId,
            lines: items.map((p: any) => ({
              material_code: p.item_code || '',
              description: p.description || '',
              quantity: String(p.quantity || 1),
              unit_price: String(p.unit_price || 0),
              gl_account: p.gl_account || '',
            })),
          }));
        }
      }
    } catch { }
  };

  const loadGlAccounts = async () => {
    try {
      const res = await authFetch(`${API_V1}/coa/accounts?per_page=200`);
      if (res.ok) { const d = await res.json(); setGlAccounts(d.accounts || d.items || []); }
    } catch { }
  };

  const onEntityChange = (entityCode: string) => {
    setForm({ ...form, entity_code: entityCode, vendor_id: '', vendor_name: '', contract_id: '', lines: [{ description: '', quantity: '', unit_price: '', gl_account: '', material_code: '' }] });
    if (entityCode) { loadVendors(entityCode); loadGlAccounts(); }
  };

  const onVendorChange = (vendorId: string) => {
    const v = vendors.find(v => v.vendor_id === vendorId);
    setForm({ ...form, vendor_id: vendorId, vendor_name: v?.vendor_name || vendorId, currency: v?.currency || 'USD', contract_id: '' });
    if (vendorId) loadContracts(vendorId);
  };

  const onContractChange = (contractId: string) => {
    if (contractId) loadPriceList(contractId);
    else setForm({ ...form, contract_id: '', lines: [{ description: '', quantity: '', unit_price: '', gl_account: '', material_code: '' }] });
  };

  const [budgetCheck, setBudgetCheck] = useState<any>(null);

  const toggleExpand = async (id: number) => {
    if (expandedId === id) { setExpandedId(null); setBudgetCheck(null); setApprovalHistory([]); return; }
    setExpandedId(id);
    setBudgetCheck(null);
    setApprovalHistory([]);
    const po = pos.find(p => p.id === id);
    // Auto-check budget for PENDING_APPROVAL POs
    if (po && po.lifecycle_status === 'PENDING_APPROVAL') {
      try {
        const res = await authFetch(`${API_V1}/budgets/check`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entity_code: po.entity_code || '', amount: po.total_amount || 0 }),
        });
        if (res.ok) setBudgetCheck(await res.json());
      } catch { }
    }
    // Load approval history for non-DRAFT POs
    if (po && po.lifecycle_status !== 'DRAFT') {
      try {
        const res = await authFetch(`${API}/${id}/approval-history`);
        if (res.ok) { const d = await res.json(); setApprovalHistory(d.timeline || []); }
      } catch { }
    }
  };

  const inputStyle: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, width: '100%', background: 'var(--bg-card)', color: 'var(--text-primary)' };

  const thStyle: React.CSSProperties = { fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)', background: 'var(--bg-card)', whiteSpace: 'nowrap', padding: '10px 14px' };
  const tdStyle: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)', borderBottom: '1px solid var(--border-subtle)' };

  return (
    <PageLayout title="Purchase Orders" subtitle="Create, approve, and issue purchase orders to vendors" action={{ label: '+ New PO', onClick: () => setShowCreate(true) }}>
      {summary && (
        <KpiGrid items={[
          { label: 'Total POs', value: summary.total, color: 'var(--text-primary)' },
          { label: 'Draft', value: summary.draft, color: 'var(--text-secondary)' },
          { label: 'Pending Approval', value: summary.pending_approval, color: 'var(--warning)' },
          { label: 'Issued', value: summary.issued, color: 'var(--positive)' },
          { label: 'Open Amount', value: formatAmountCompact(summary.open_amount), color: 'var(--accent)' },
        ]} />
      )}

      <FilterBar filters={[
        { key: 'status', label: 'Statuses', type: 'select', value: statusFilter, onChange: v => { setStatusFilter(v); setPage(0); }, options: ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'ISSUED', 'PARTIALLY_RECEIVED', 'FULLY_RECEIVED', 'CLOSED'].map(s => ({ value: s, label: s })), width: 200 },
      ]} />

      {loading ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      ) : pos.length === 0 ? (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', padding: 40, textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>No purchase orders found.</div>
      ) : (
        <div style={{ background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              {[{ key: 'po', label: 'PO #' }, { key: 'vendor', label: 'Vendor' }, { key: 'amount', label: 'Amount', align: 'right' as const }, { key: 'entity', label: 'Entity' }, { key: 'status', label: 'Status' }, { key: 'actions', label: 'Actions' }].map(h => (
                <th key={h.key} style={{ ...thStyle, textAlign: h.align || 'left' }}>{h.label}</th>
              ))}
            </tr></thead>
            <tbody>
              {pos.map(po => (
                <>
                  <tr key={po.id} onClick={() => toggleExpand(po.id)} style={{ cursor: 'pointer', background: expandedId === po.id ? 'var(--accent-subtle, #f0f4f8)' : 'transparent' }}>
                    <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--accent)' }}>{po.po_number}</td>
                    <td style={tdStyle}>{po.vendor_name || po.vendor_id}</td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{formatAmount(po.total_amount)}</td>
                    <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{po.entity_code}</td>
                    <td style={tdStyle}><StatusBadge status={po.lifecycle_status} /></td>
                    <td style={tdStyle} onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {po.lifecycle_status === 'DRAFT' && <ActionButton label="Submit" onClick={() => handleAction(po.id, 'submit-approval', po.version)} variant="secondary" size="sm" />}
                        {po.lifecycle_status === 'PENDING_APPROVAL' && <ActionButton label="Approve" onClick={() => handleAction(po.id, 'approve', po.version)} variant="success" size="sm" disabled={!canApprove(po.total_amount || 0, po.entity_code, 'PO')} />}
                        {po.lifecycle_status === 'APPROVED' && <ActionButton label="Issue" onClick={() => handleAction(po.id, 'issue', po.version)} variant="primary" size="sm" />}
                        {po.lifecycle_status !== 'DRAFT' && <ActionButton label="PDF" onClick={async () => { try { const res = await authFetch(`${API}/${po.id}/pdf`); if (res.ok) { const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `PO_${po.po_number}.pdf`; a.click(); URL.revokeObjectURL(url); } else showToast('PDF generation failed', false); } catch { showToast('PDF download failed', false); } }} variant="secondary" size="sm" />}
                      </div>
                    </td>
                  </tr>
                  {expandedId === po.id && po.lines && (
                    <tr key={`${po.id}-lines`}><td colSpan={6} style={{ padding: '0 14px 12px 32px', background: 'var(--accent-subtle, #f0f4f8)' }}>
                      {/* Budget check for pending approval */}
                      {budgetCheck && po.lifecycle_status === 'PENDING_APPROVAL' && (
                        <div style={{ display: 'flex', gap: 10, marginTop: 8, marginBottom: 8, padding: '10px 12px', borderRadius: 6, border: `1px solid ${budgetCheck.status === 'OVER_BUDGET' ? 'var(--negative)' : budgetCheck.status === 'WARNING' ? 'var(--warning)' : 'var(--positive)'}`, background: budgetCheck.status === 'OVER_BUDGET' ? 'var(--negative-bg)' : 'var(--bg-card)' }}>
                          {[
                            { label: 'Budget', value: formatAmount(budgetCheck.budget_amount), color: 'var(--text-primary)' },
                            { label: 'YTD Spent', value: formatAmount(budgetCheck.ytd_actuals), color: 'var(--text-secondary)' },
                            { label: 'Committed', value: formatAmount(budgetCheck.committed), color: 'var(--warning)' },
                            { label: 'This PO', value: formatAmount(budgetCheck.this_transaction), color: 'var(--accent)' },
                            { label: 'Remaining After', value: formatAmount(budgetCheck.remaining_after), color: budgetCheck.remaining_after < 0 ? 'var(--negative)' : 'var(--positive)' },
                            { label: 'Utilization', value: `${budgetCheck.utilization_pct}%`, color: budgetCheck.utilization_pct > 90 ? 'var(--negative)' : 'var(--text-primary)' },
                          ].map(s => (
                            <div key={s.label} style={{ flex: 1, textAlign: 'center' }}>
                              <div style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.value}</div>
                              <div style={{ fontSize: 9, color: 'var(--text-secondary)' }}>{s.label}</div>
                            </div>
                          ))}
                          {budgetCheck.warning && <div style={{ flex: 2, fontSize: 11, fontWeight: 600, color: budgetCheck.status === 'OVER_BUDGET' ? 'var(--negative)' : 'var(--warning)', display: 'flex', alignItems: 'center' }}>{budgetCheck.warning}</div>}
                          {!budgetCheck.has_budget && <div style={{ flex: 2, fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic', display: 'flex', alignItems: 'center' }}>No budget defined for this entity/period</div>}
                        </div>
                      )}
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 8 }}>
                        <thead><tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          {['#', 'Material', 'Description', 'Qty', 'Price', 'Amount', 'GL', 'Received'].map(h => (
                            <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase' as const }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>{po.lines.map(l => (
                          <tr key={l.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '10px 14px', fontSize: 13 }}>{l.line_number}</td>
                            <td style={{ padding: '10px 14px', fontSize: 13 }}>{l.material_code || '-'}</td>
                            <td style={{ padding: '10px 14px', fontSize: 13 }}>{l.description}</td>
                            <td style={{ padding: '10px 14px', fontSize: 13 }}>{l.quantity}</td>
                            <td style={{ padding: '10px 14px', fontSize: 13 }}>${l.unit_price?.toFixed(2)}</td>
                            <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 600 }}>${l.line_amount?.toFixed(2)}</td>
                            <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-secondary)' }}>{l.gl_account || '-'}</td>
                            <td style={{ padding: '10px 14px', fontSize: 13 }}>{l.received_quantity}/{l.quantity}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                      {/* Approval Chain Timeline */}
                      {approvalHistory.length > 0 && (
                        <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid var(--border-subtle)' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Approval Chain</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            {approvalHistory.map((e: any, i: number) => {
                              const color = e.event === 'APPROVED' ? 'var(--positive)' : e.event === 'REJECTED' ? 'var(--negative)' : e.event === 'ISSUED' ? 'var(--accent)' : 'var(--text-secondary)';
                              const icon = e.event === 'CREATED' ? '\u25CB' : e.event === 'SUBMITTED' ? '\u25B6' : e.event === 'APPROVED' ? '\u2713' : e.event === 'REJECTED' ? '\u2717' : e.event === 'ISSUED' ? '\u2605' : '\u25CF';
                              return (
                                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', paddingBottom: 8, paddingTop: 4, borderLeft: i < approvalHistory.length - 1 ? '2px solid var(--border-subtle)' : '2px solid transparent', marginLeft: 8, paddingLeft: 16, position: 'relative' }}>
                                  <div style={{ position: 'absolute', left: -7, top: 6, width: 12, height: 12, borderRadius: '50%', background: 'var(--bg-card)', border: `2px solid ${color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color, fontWeight: 700 }}>{icon}</div>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                      <span style={{ fontSize: 12, fontWeight: 600, color }}>{e.event}</span>
                                      {e.doa_level && <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: 'var(--accent-subtle, #eef2ff)', color: 'var(--accent)', fontWeight: 600 }}>{e.doa_level}</span>}
                                      {e.department && <span style={{ fontSize: 10, color: 'var(--text-hint)' }}>{e.department}</span>}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                                      {e.by_name && <span style={{ fontWeight: 600 }}>{e.by_name}</span>}
                                      {e.at && <span style={{ marginLeft: 8, color: 'var(--text-hint)' }}>{new Date(e.at).toLocaleString()}</span>}
                                    </div>
                                    {e.notes && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2, fontStyle: 'italic' }}>{e.notes}</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </td></tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <ActionButton label="PREV" onClick={() => setPage(Math.max(0, page - 1))} variant="secondary" size="sm" disabled={page === 0} />
          <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
          <ActionButton label="NEXT" onClick={() => setPage(page + 1)} variant="secondary" size="sm" disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      )}

      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create Purchase Order" size="lg" footer={<><ActionButton label="Cancel" onClick={() => setShowCreate(false)} variant="secondary" /><ActionButton label="Create PO" onClick={handleCreate} /></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Step 1: Entity */}
          <select value={form.entity_code} onChange={e => onEntityChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">1. Select Legal Entity *</option>
            {entities.map(e => <option key={e.entity_code} value={e.entity_code}>{e.entity_code} — {e.entity_name} ({e.functional_currency})</option>)}
          </select>

          {/* Step 2: Vendor (dropdown from active vendors) */}
          {form.entity_code && (
            <select value={form.vendor_id} onChange={e => onVendorChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
              <option value="">2. Select Vendor *</option>
              {vendors.map(v => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_id} — {v.vendor_name}</option>)}
            </select>
          )}

          {/* Step 3: Contract (optional — loads price list) */}
          {form.vendor_id && contracts.length > 0 && (
            <select value={form.contract_id} onChange={e => onContractChange(e.target.value)} style={{ ...inputStyle, cursor: 'pointer', borderColor: 'var(--positive)' }}>
              <option value="">3. Select Contract (auto-fills lines)</option>
              {contracts.map((c: any) => <option key={c.id} value={c.id}>{c.contract_number} — {c.title || c.contract_type} ({formatAmount(c.total_value)})</option>)}
            </select>
          )}
          {form.vendor_id && contracts.length === 0 && (
            <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic' }}>No active contracts for this vendor — enter lines manually</div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <input placeholder="PO Number (auto-generated if blank)" value={form.po_number} onChange={e => setForm({ ...form, po_number: e.target.value })} style={inputStyle} />
            <select title="Currency" value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })} style={{ ...inputStyle, maxWidth: 100 }}>
              {['USD','EUR','GBP','SAR','AED','BRL','CHF','JPY','CNY','INR','CAD','AUD','SGD','HKD','KWD','QAR','BHD','OMR','EGP','JOD','TRY','ZAR','MXN','KRW','SEK','NOK','DKK','PLN','CZK','HUF'].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {/* Line items — auto-populated from contract or manual */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 700, marginBottom: 8, color: form.contract_id ? 'var(--positive)' : 'var(--text-secondary)' }}>
              {form.contract_id ? `LINE ITEMS (from contract — ${form.lines.length} items)` : 'LINE ITEMS'}
            </div>
            {form.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                <input placeholder="Item Code" value={l.material_code} onChange={e => updateLine(i, 'material_code', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="Description" value={l.description} onChange={e => updateLine(i, 'description', e.target.value)} style={{ ...inputStyle, flex: 3 }} />
                <input placeholder="Qty" type="number" value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} style={{ ...inputStyle, flex: 1 }} />
                <input placeholder="Price" type="number" value={l.unit_price} onChange={e => updateLine(i, 'unit_price', e.target.value)} style={{ ...inputStyle, flex: 1, background: form.contract_id ? 'var(--accent-subtle, #eef2ff)' : 'var(--bg-card)' }} readOnly={!!form.contract_id} title={form.contract_id ? 'Price from contract — locked' : ''} />
                <select title="GL Account" value={l.gl_account} onChange={e => updateLine(i, 'gl_account', e.target.value)} style={{ ...inputStyle, flex: 2 }}>
                  <option value="">GL Account</option>
                  {glAccounts.map(a => <option key={a.account_code} value={a.account_code}>{a.account_code} — {a.account_name}</option>)}
                </select>
              </div>
            ))}
            <button onClick={addLine} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Add Line</button>
          </div>
        </div>
      </Modal>

      {toast && <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 2000, padding: '12px 20px', borderRadius: 8, background: toast.ok ? 'var(--positive-bg)' : 'var(--negative-bg)', color: toast.ok ? 'var(--positive)' : 'var(--negative)', fontSize: 13, fontWeight: 600 }}>{toast.msg}</div>}
    </PageLayout>
  );
}
